/**
 * Lire et écrire le consentement newsletter (ROADMAP 6.3).
 *
 * ══ Ce que cette couche fait, et surtout ce qu'elle NE fait pas ═════════════
 *
 * Elle n'a **aucune règle** : l'état courant se déduit du journal dans
 * `core/newsletter.ts`, et c'est là qu'il se teste. Ici, il n'y a qu'un aller-retour
 * réseau et sa dégradation.
 *
 * ⚠️ **Un échec réseau ne doit jamais afficher une case cochée.** C'est la seule décision
 * de ce fichier, et elle penche toujours du même côté : sans réponse, on rend « pas de
 * consentement ». Se tromper dans ce sens ne coûte qu'une case à recocher ; se tromper
 * dans l'autre ferait croire à un consentement qui n'existe pas, et l'envoi serait
 * illicite.
 *
 * ══ Pourquoi le journal n'est pas mis en cache ══════════════════════════════
 *
 * Un consentement se retire, et un retrait doit prendre effet immédiatement. Un cache
 * local ferait survivre l'ancien état à un désabonnement fait depuis un autre appareil —
 * exactement le cas où le joueur constate que rien n'a changé.
 */

import { currentConsent, NEWSLETTER_WORDING_VERSION } from '@/core/newsletter';
import { getLocale } from '@/i18n';

import {
  currentAuthSession,
  ensureAuthSession,
  getSupabase,
  isSupabaseConfigured,
} from './supabase';

/**
 * Le joueur est-il abonné, à cet instant ?
 *
 * Rend `false` dès que la réponse n'est pas certaine — voir l'en-tête.
 */
export async function fetchNewsletterConsent(): Promise<boolean> {
  if (!isSupabaseConfigured()) return false;

  /*
    L'identité d'abord, pour la même raison que `fetchPaidGemsTotal` : la policy de
    lecture est accordée à `authenticated` seul. Appelée avant l'ouverture de la session
    anonyme, la requête rend 401 et inscrit une erreur dans la console de chaque joueur —
    que le parcours enchaîné tient pour contractuellement vide.
  */
  /*
    ⚠️ `currentAuthSession`, PAS `ensureAuthSession` — la LECTURE n'enrôle pas.

    C'était le dernier des trois appelants qui mintaient un compte anonyme au démarrage,
    et le plus difficile à trouver : il est derrière un drapeau, dans un `.then` de
    `account.refresh()`, et rien à l'écran ne le trahit. Il a fallu tracer la pile depuis
    le bundle minifié pour le nommer.

    Le raisonnement est le même que pour le registre des gemmes : **le consentement est
    indexé par propriétaire.** Sans identité, il ne peut pas y en avoir un — on créait
    donc un compte pour interroger une table qui ne pouvait rien rendre.

    L'ÉCRITURE, elle, garde `ensureAuthSession` : donner son consentement est un geste
    délibéré du joueur, et il mérite une identité.
  */
  if ((await currentAuthSession()) === null) return false;

  try {
    const { data, error } = await getSupabase()
      .from('newsletter_consents')
      .select('granted, created_at');

    // Le client typé garantit `data` non nul quand `error` l'est : une seconde garde
    // serait une condition morte, et ESLint la refuse à juste titre.
    if (error !== null) return false;
    return currentConsent(data);
  } catch {
    return false;
  }
}

/**
 * Consigne un acte de volonté — consentement ou retrait.
 *
 * Rend `true` si la ligne est écrite. L'appelant s'en sert pour n'afficher la case
 * cochée qu'une fois la preuve **réellement** enregistrée : cocher une case que le
 * serveur a refusée montrerait un abonnement qui n'existe pas.
 */
export async function recordNewsletterConsent(granted: boolean, email: string): Promise<boolean> {
  if (!isSupabaseConfigured()) return false;

  const ownerId = await ensureAuthSession();
  if (ownerId === null) return false;

  try {
    const { error } = await getSupabase().from('newsletter_consents').insert({
      owner_id: ownerId,
      granted,
      email,
      // La version du TEXTE accepté : on ne consent pas « à la newsletter », on consent
      // à une formulation. Voir `core/newsletter.ts`.
      wording_version: NEWSLETTER_WORDING_VERSION,
      locale: getLocale(),
    });
    return error === null;
  } catch {
    return false;
  }
}
